import type {
	ColumnFilter,
	ColumnFiltersState,
	ColumnSort,
	ExpandedState,
	RowSelectionState,
	VisibilityState,
} from "@tanstack/react-table";
import _ from "lodash";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type TableStore = {
	globalFilter: string;
	setGlobalFilter: (value: string) => void;
	columnVisibility: VisibilityState;
	setColumnVisibility: (data: () => VisibilityState) => void;
	columnFilters: ColumnFiltersState;
	setColumnFilters: (id: string, data: string[]) => void;
	resetColumnFilters: () => void;
	columnSorting: ColumnSort[];
	setColumnSorting: (data: () => ColumnSort[]) => void;
	rowSelection: RowSelectionState;
	setRowSelection: (data: () => RowSelectionState) => void;
	pageIndex: number;
	setPageIndex: (data: number) => void;
	pageSize: number;
	setPageSize: (data: number) => void;
	expanded: ExpandedState;
	setExpanded: (data: () => ExpandedState) => void;
};

export const useTableState = create<TableStore>()(
	persist(
		(set, get) => ({
			globalFilter: "",
			setGlobalFilter: (data) => set(() => ({ globalFilter: data })),
			columnVisibility: {},
			setColumnVisibility: (data) => set(() => ({ columnVisibility: data() })),
			columnFilters: [],
			setColumnFilters: (columnId, columnValue) =>
				set((old) => {
					const m = new Map<string, string[]>(
						old.columnFilters.map((item) => [item.id, item.value as string[]]),
					);
					if (columnValue.length > 0) {
						m.set(columnId, columnValue);
					} else {
						m.delete(columnId);
					}
					return {
						columnFilters: Object.entries(Object.fromEntries(m)).map((i) => ({
							id: i[0],
							value: i[1],
						})),
					};
				}),
			resetColumnFilters: () => set(() => ({ columnFilters: [] })),
			columnSorting: [],
			setColumnSorting: (data) => set(() => ({ columnSorting: data() })),
			rowSelection: {},
			setRowSelection: (data) => set(() => ({ rowSelection: data() })),
			pageIndex: 0,
			setPageIndex: (data) => set(() => ({ pageIndex: data })),
			pageSize: 10,
			setPageSize: (data) => set(() => ({ pageSize: data })),
			expanded: {},
			setExpanded: (data) => set(() => ({ expanded: data() })),
		}),
		{
			name: "yehey-table", // unique name
			storage: createJSONStorage(() => sessionStorage), // (optional) by default, 'localStorage' is used
		},
	),
);

type TableStoreDefaultSettings = {
	defaultColumnVisibility?: VisibilityState;
	defaultColumnFilters?: ColumnFilter[];
	defaultColumnSorting?: ColumnSort[];
	defaultRowSelection?: RowSelectionState;
	defaultPageIndex?: number;
	defaultPageSize?: number;
	expanded: ExpandedState;
};

const defaultTableSettings: TableStoreDefaultSettings = {
	defaultColumnVisibility: {},
	defaultColumnFilters: [],
	defaultColumnSorting: [],
	defaultRowSelection: {},
	defaultPageIndex: 0,
	defaultPageSize: 10,
	expanded: {},
};
// factory
export const createTableStore = (
	name: string,
	defaultSettings = defaultTableSettings,
) =>
	create<TableStore>()(
		persist(
			(set, _get) => ({
				globalFilter: "",
				setGlobalFilter: (data) => set(() => ({ globalFilter: data })),
				columnVisibility: defaultSettings?.defaultColumnVisibility || {},
				setColumnVisibility: (data) =>
					set((old) => ({
						columnVisibility: Object.fromEntries([
							...Object.entries(old.columnVisibility),
							...Object.entries(data()),
						]),
					})),
				columnFilters: defaultSettings?.defaultColumnFilters || [],
				setColumnFilters: (columnId, columnValue) =>
					set((old) => {
						const m = new Map<string, string[]>(
							old.columnFilters.map((item) => [
								item.id,
								item.value as string[],
							]),
						);
						if (columnValue.length > 0) {
							m.set(columnId, columnValue);
						} else {
							m.delete(columnId);
						}
						return {
							columnFilters: Object.entries(Object.fromEntries(m)).map((i) => ({
								id: i[0],
								value: i[1],
							})),
						};
					}),
				resetColumnFilters: () => set(() => ({ columnFilters: [] })),
				columnSorting: defaultSettings.defaultColumnSorting || [],
				setColumnSorting: (data) => set(() => ({ columnSorting: data() })),
				rowSelection: defaultSettings.defaultRowSelection || {},
				setRowSelection: (data) => set(() => ({ rowSelection: data() })),
				pageIndex: 0,
				setPageIndex: (data) => set(() => ({ pageIndex: data })),
				pageSize: defaultSettings.defaultPageSize || 10,
				setPageSize: (data) => set(() => ({ pageSize: data })),
				expanded: {},
				setExpanded: (data) => {
					set((old) => {
						const g = Object.entries(data())[0];

						const isExist = old.expanded[g[0]];

						if (isExist) {
							const n = Object.entries(old.expanded).map((i) => {
								if (i[0] === g[0]) {
									return [g[0], !i];
								}
								return i;
							});
							return { expanded: Object.fromEntries(n) };
						}
						return { expanded: { ...old.expanded, ...data() } };
					});
				},
			}),
			{
				name: `yehey-table:${name}`, // unique name
				storage: createJSONStorage(() => sessionStorage), // (optional) by default, 'localStorage' is used
			},
		),
	);
